import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { SimulationRunnerService } from '@services/simulation-set/simulation-runner.service';
import { SimulationSetService } from '@services/simulation-set/simulation-set.service';
import { BannerNotificationComponent } from '@core/notification-banner/notification-banner.component';
import { NotificationBanner } from '@models/notification-banner';
import { PermissionsService } from '@services/auth/permissions.service';
import { MenuService } from '@services/menu/menu.service';
import { Subject } from 'rxjs';
import { BannerDisplayed } from '@models/banner-displayed';
import { DatasetsService } from '@services/datasets/datasets.service';
import { ResultsStoreService } from '@services/results/results-store.service';
import { takeUntil, filter, first, skip, take, map, tap } from 'rxjs/operators';
import { NotificationsService } from '@services/notifications/notifications.service';
import { GlobalParamsService } from '@services/global-params/global-params.service';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { devLog } from '@helpers/dev-log';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  // all the banner related methods and templates could be
  // externalized to his own component like MainNotificationBannerComponent If is needed
  @ViewChild('mainNotificationBanner_import', { static: false })
  public mainNotificationBanner_import: BannerNotificationComponent;
  @ViewChild('mainNotificationBanner_numPeriods', { static: false })
  public mainNotificationBanner_numPeriods: BannerNotificationComponent;
  @ViewChild(ToastContainerDirective, { static: true })
  public toastContainer: ToastContainerDirective;

  get importDisplayConditions() {
    return (
      this.permissions.getPermission('Import') === 'W' && !this.banners.find(banner => banner.name === 'import').dismiss
    );
  }

  constructor(
    private permissions: PermissionsService,
    private menu: MenuService,
    public sim: SimulationSetService,
    public runner: SimulationRunnerService,
    private datasetsService: DatasetsService,
    private resultsStore: ResultsStoreService,
    private notifications: NotificationsService,
    private globalParams: GlobalParamsService,
    private changeRef: ChangeDetectorRef,
    private toastrService: ToastrService
  ) {}

  private destroyed$ = new Subject();
  public bannersDisplayed: BannerDisplayed[];

  public importBanner: NotificationBanner = {
    title: '',
    body: `New Data available for Import & Aggregation`,
    actions: [
      {
        // Dismiss
        name: 'dismiss',
        wording: 'Dismiss',
        primary: false,
        clickHandler: $event => this.dismissNotificationBanner('import', true)
      },
      {
        // Load Latest
        name: 'go-to-import',
        wording: 'GO TO IMPORT PAGE',
        primary: true,
        clickHandler: $event => {
          // Close previous dialog with some delay
          const previousOpenedDialog = this.menu.dialogRef;
          if (previousOpenedDialog) setTimeout(() => previousOpenedDialog.close(), 300);

          this.menu.goToMenuItem('sim-importer');
          document.getElementById('btn_importer').click(); // TODO: Improve this to don't be necessary to fake a click event
        }
      }
    ]
  };
  public numPeriodsBanner: NotificationBanner = {
    title: '',
    body: `The number of periods in the system has changed. Your resultset is therefore out of date. Please rerun.`,
    actions: [
      {
        // Dismiss
        name: 'dismiss',
        wording: 'Dismiss',
        primary: false,
        clickHandler: $event => this.dismissNotificationBanner('numPeriods', true)
      },
      {
        // Load Latest
        name: 'go-to-simulation',
        wording: 'Rerun',
        primary: true,
        clickHandler: $event => {
          // Close previous dialog with some delay
          const previousOpenedDialog = this.menu.dialogRef;
          if (previousOpenedDialog) setTimeout(() => previousOpenedDialog.close(), 300);

          this.menu.goToMenuItem('simulation-set');
          // TODO: Improve this to don't be necessary to fake a click event to open the dialog
          document.getElementById('btn_create_edit_simulation').click();
        }
      }
    ]
  };

  private banners: BannerDisplayed[] = [
    { name: 'import', display: false, dismiss: false, banner: this.importBanner },
    { name: 'numPeriods', display: false, dismiss: false, banner: this.numPeriodsBanner }
  ];

  ngOnInit() {
    // Ordered by importance. If multiple banners has display true, the first found in the array will be displayed only (lower index)
    ['import', 'numPeriods'].forEach(bannerName => this.initBannerDisplayChecker(bannerName));

    this.toastrService.overlayContainer = this.toastContainer;
    this.notifications.mainNotificationBanner$
      .pipe(
        takeUntil(this.destroyed$),
        filter(x => x) // only IF is truly
      )
      .subscribe(params => {
        if (params.action === 'display') this.displayNotificationBanner(params.name);
        if (params.action === 'dismiss') this.dismissNotificationBanner(params.name, false);
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private getNotificationBanner(name: string) {
    if (name === 'import') return this.mainNotificationBanner_import;
    if (name === 'numPeriods') return this.mainNotificationBanner_numPeriods;
  }

  public dismissNotificationBanner(name: string, dismiss: boolean) {
    try {
      if (this.getNotificationBanner(name)) {
        this.getNotificationBanner(name)
          .asyncDismissBannerNotification()
          .then(() => {
            const theBanner = this.banners.find(banner => banner.display && (name ? name === banner.name : true));
            theBanner.dismiss = dismiss;
            theBanner.display = false;
            this.onBannersDisplayedChanged();
          });
      }
    } catch (err) {
      console.warn('Unable to dismiss the banner message with name:', name);
    }
  }

  public displayNotificationBanner(bannerName: string) {
    try {
      const banner = this.banners.find(b => b.name === bannerName);
      banner.display = true;
      banner.dismiss = false;
      this.onBannersDisplayedChanged();
    } catch (err) {
      console.warn('Unable to display the banner message with name:', bannerName);
    }
  }

  // Checks conditions on the first run
  private initBannerDisplayChecker(bannerName: string) {
    if (bannerName === 'import') {
      // IMPORT BANNER
      // -------------
      // Display it
      // -- Checks for IMPORT_AVAILABLE on the first run
      this.datasetsService.importSettings$
        .pipe(
          takeUntil(this.destroyed$),
          skip(1), // ignore the initial value of the BS Initialization
          map(importSettings => importSettings && importSettings['Import_Available']),
          // We only want the first value once results are loaded and Import_Available is truly. If not, Ignore it and unsubscribe
          first(),
          filter(x => x) // Allow only truly values
        )
        .subscribe(importAvailable => {
          try {
            this.displayNotificationBanner(bannerName);
          } catch (err) {
            console.error('Unable to display the import banner message (1).', err);
          }
        });

      return;
    }

    if (bannerName === 'numPeriods') {
      // NUMBER OF PERIODS CHANGED BANNER
      // --------------------------------
      // Number of periods of ran simulations are different of the current global params number of periods ?
      this.resultsStore.numPeriodsOfRanSimulations$
        .pipe(
          takeUntil(this.destroyed$),
          skip(1) // Skip initialization (null)
          // Once it reaches this point, the global params are available
        )
        .subscribe(ranPeriodsList => {
          try {
            const displayCond =
              ranPeriodsList &&
              ranPeriodsList.length > 0 &&
              !ranPeriodsList.every(ranPeriod => ranPeriod === Number(this.globalParams.numPeriods));

            if (displayCond) this.displayNotificationBanner(bannerName);
            else this.dismissNotificationBanner(bannerName, false);
          } catch (err) {
            console.error('Unable to display the numPeriods banner message', err);
          }
        });

      return;
    }
  }

  private onBannersDisplayedChanged() {
    // Update the references
    this.bannersDisplayed = this.banners.filter(banner => banner.display);
    // We need to detectChanges before calling the reference
    this.changeRef.detectChanges();
    this.bannersDisplayed
      .filter(bannerDisplayed => bannerDisplayed && bannerDisplayed.display && !bannerDisplayed.dismiss)
      .forEach(toDisplay => (this.getNotificationBanner(toDisplay.name).showBannerNotification = true));
  }
}
